import Bugsnag, { type Event } from '@bugsnag/js';

import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from 'utils/env';

// FIXME sc-169532 - Temporary diagnostics for unexpected logouts. Remove once the cause has been found.
// Must stay synchronous, passive and never throw, so the logout flow and redirect are unchanged.

const LAST_USER_KEY = 'authDiag:lastUser';
// extended while the user is active, so only users inactive this long lose attribution
const LAST_USER_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const LAST_USER_REFRESH_MS = 24 * 60 * 60 * 1000;
// many requests fail at once when the session is gone, Bugsnag only keeps the last 25 breadcrumbs
const TOKEN_ERROR_BREADCRUMB_INTERVAL_MS = 60 * 1000;

type LastUser = { sub: string; at: number; expiresAt: number };

type SilentAuthResult = { error: string; description?: string; at: number };

// `in_progress` until the Auth0Provider has loaded, then the first iframe answer or, without one,
// `not_attempted` / `without_iframe` / `no_response` / `unknown` (see markAuthStartupComplete)
type StartupSilentAuth =
  | 'unknown'
  | 'not_attempted'
  | 'redirect_callback'
  | 'in_progress'
  | 'without_iframe'
  | 'no_response'
  | SilentAuthResult;

type DiagnosticsArgs = {
  isAuthenticated?: boolean;
  hasUser?: boolean;
  // auth0 errors keep the OAuth code in `error`, Apollo/GraphQL errors are passed as is
  error?: unknown;
  broadcast?: boolean;
  reAuthAttempts?: number;
};

// auth0-spa-js only attempts silent auth on page load when one of these exists, and removes
// them when silent auth fails. Other apps' `*is.authenticated` cookies are reported but ignored.
const AUTH0_IS_AUTHENTICATED_COOKIES = [
  `auth0.${AUTH0_CLIENT_ID}.is.authenticated`,
  'auth0.is.authenticated',
].flatMap(name => [name, `_legacy_${name}`]);
const IS_AUTHENTICATED_COOKIE_SUFFIX = 'is.authenticated';
// same check as auth0-react, which handles the login redirect instead of silent auth
const REDIRECT_CALLBACK_RE = /[?&](?:(?:connect_)?code|error)=[^&]+/;
const STATE_RE = /[?&]state=[^&]+/;

// per tab, survives another tab clearing the shared localStorage on logout
let sessionUser: LastUser | undefined;
let startupSilentAuth: StartupSilentAuth | undefined;
let lastSilentAuth: SilentAuthResult | undefined;
let isAuthenticatedCookiesAtStartup: string[] | undefined;
// time of the last breadcrumb per distinct token error
const tokenErrorBreadcrumbs = new Map<string, number>();

// lets a later "logged out" event be attributed to a user, Bugsnag only knows it after login
export function rememberAuthUser({ sub }: { sub?: string }) {
  if (!sub) {
    return;
  }
  const at = Date.now();
  sessionUser = { sub, at, expiresAt: at + LAST_USER_RETENTION_MS };
  try {
    localStorage.setItem(LAST_USER_KEY, JSON.stringify(sessionUser));
  } catch {
    // storage unavailable, nothing to remember
  }
}

// called when the Session effect re-runs for the same user, writes at most once a day
export function extendAuthUserRetention() {
  if (!sessionUser) {
    return;
  }
  const expiresAt = Date.now() + LAST_USER_RETENTION_MS;
  if (expiresAt - sessionUser.expiresAt < LAST_USER_REFRESH_MS) {
    return;
  }
  sessionUser = { ...sessionUser, expiresAt };
  try {
    const stored: Partial<LastUser> | null = JSON.parse(
      localStorage.getItem(LAST_USER_KEY) ?? 'null'
    );
    // never resurrect a record removed by a logout, or overwrite another user's
    if (stored?.sub !== sessionUser.sub) {
      return;
    }
    // keep a newer session start that another tab stored for the same user
    const at = typeof stored.at === 'number' ? Math.max(stored.at, sessionUser.at) : sessionUser.at;
    localStorage.setItem(LAST_USER_KEY, JSON.stringify({ ...sessionUser, at }));
  } catch {
    // storage unavailable, nothing to extend
  }
}

export function forgetAuthUser() {
  sessionUser = undefined;
  try {
    localStorage.removeItem(LAST_USER_KEY);
  } catch {
    // storage unavailable, nothing to forget
  }
}

// read-only so it is safe during render, expired entries are removed in initAuthDiagnostics
function readLastUser(): LastUser | null {
  try {
    const lastUser: LastUser | null = JSON.parse(localStorage.getItem(LAST_USER_KEY) ?? 'null');
    return lastUser?.sub && lastUser.expiresAt > Date.now() ? lastUser : null;
  } catch {
    return null;
  }
}

// Call before the Auth0Provider mounts. Silent auth posts its result from a hidden iframe,
// which `checkSession()` swallows, so listen for the same message.
export function initAuthDiagnostics() {
  try {
    const auth0Origin = new URL(
      AUTH0_DOMAIN.startsWith('http') ? AUTH0_DOMAIN : `https://${AUTH0_DOMAIN}`
    ).origin;
    isAuthenticatedCookiesAtStartup = getIsAuthenticatedCookies();
    if (!readLastUser()) {
      forgetAuthUser();
    }
    const { search } = window.location;
    startupSilentAuth =
      REDIRECT_CALLBACK_RE.test(search) && STATE_RE.test(search)
        ? 'redirect_callback'
        : 'in_progress';

    const handleMessage = ({ origin, data }: MessageEvent) => {
      if (origin !== auth0Origin || data?.type !== 'authorization_response') {
        return;
      }
      const { error, error_description } = data.response ?? {};
      lastSilentAuth = { error: error ?? 'ok', description: error_description, at: Date.now() };
      // the provider's token requests are serialized, so the first answer belongs to checkSession()
      if (startupSilentAuth === 'in_progress') {
        startupSilentAuth = lastSilentAuth;
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      sessionUser = undefined;
      tokenErrorBreadcrumbs.clear();
      startupSilentAuth = undefined;
      lastSilentAuth = undefined;
      isAuthenticatedCookiesAtStartup = undefined;
    };
  } catch {
    return () => {};
  }
}

type ErrorFields = { name?: string; message?: string; error?: string };

// some rejections are plain strings, e.g. the default AuthContext
function toErrorFields(e: unknown): ErrorFields {
  return typeof e === 'string' ? { message: e } : ((e ?? {}) as ErrorFields);
}

// called from a catch block, so it must not replace the error being rethrown
export function leaveTokenErrorBreadcrumb(e: unknown) {
  try {
    const { error, message } = toErrorFields(e);
    const key = `${error}:${message}`;
    const now = Date.now();
    tokenErrorBreadcrumbs.forEach((at, seenKey) => {
      if (now - at >= TOKEN_ERROR_BREADCRUMB_INTERVAL_MS) {
        tokenErrorBreadcrumbs.delete(seenKey);
      }
    });
    if (tokenErrorBreadcrumbs.has(key)) {
      return;
    }
    tokenErrorBreadcrumbs.set(key, now);
    Bugsnag.leaveBreadcrumb('getAccessTokenSilently failed', { error, message }, 'error');
  } catch {
    // diagnostics only
  }
}

// call once the Auth0Provider has finished loading
export function markAuthStartupComplete({ isAuthenticated }: { isAuthenticated: boolean }) {
  if (startupSilentAuth !== 'in_progress') {
    return;
  }
  if (!isAuthenticatedCookiesAtStartup) {
    // cookies could not be read at startup, so whether the SDK tried silent auth is unknown
    startupSilentAuth = 'unknown';
    return;
  }
  const hadAuth0Cookie = isAuthenticatedCookiesAtStartup.some(name =>
    AUTH0_IS_AUTHENTICATED_COOKIES.includes(name)
  );
  if (!hadAuth0Cookie) {
    startupSilentAuth = 'not_attempted';
  } else {
    // with `useRefreshTokensFallback` a failed refresh token falls back to the iframe, so
    // authenticated without an iframe answer means the SDK did not need one
    startupSilentAuth = isAuthenticated ? 'without_iframe' : 'no_response';
  }
}

// undefined when cookies cannot be read, so the rest of the report is still sent
function getIsAuthenticatedCookies() {
  try {
    return document.cookie
      .split('; ')
      .map(cookie => cookie.split('=')[0])
      .filter(name => name.endsWith(IS_AUTHENTICATED_COOKIE_SUFFIX));
  } catch {
    return undefined;
  }
}

function describeError(error: unknown) {
  if (!error) {
    return {};
  }
  const { name, message, error: code } = toErrorFields(error);
  return { error: [name, message].filter(Boolean).join(': '), errorCode: code };
}

// values are read when called, so call before clearing cookies or storage. Read-only.
export function withAuthDiagnostics({
  isAuthenticated,
  hasUser,
  error,
  broadcast,
  reAuthAttempts,
}: DiagnosticsArgs = {}) {
  try {
    const lastUser = sessionUser ?? readLastUser();
    const navigation = performance.getEntriesByType?.('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;

    const metadata = {
      isAuthenticated,
      hasUser,
      ...describeError(error),
      // logout() only: false when this tab is following another tab's USER_LOGOUT broadcast
      broadcast,
      reAuthAttempts,
      isAuthenticatedCookiesAtStartup,
      isAuthenticatedCookies: getIsAuthenticatedCookies(),
      startupSilentAuthResult:
        typeof startupSilentAuth === 'object' ? startupSilentAuth.error : startupSilentAuth,
      startupSilentAuthDescription:
        typeof startupSilentAuth === 'object' ? startupSilentAuth.description : undefined,
      // latest response from any silent auth since the page loaded, including later token requests
      lastSilentAuthResult: lastSilentAuth?.error,
      lastSilentAuthDescription: lastSilentAuth?.description,
      msSinceLastSilentAuth: lastSilentAuth ? Date.now() - lastSilentAuth.at : undefined,
      navigationType: navigation?.type,
      visibilityState: document.visibilityState,
      onLine: navigator.onLine,
      cookieEnabled: navigator.cookieEnabled,
      referrer: document.referrer || undefined,
    };

    const msSinceLastSessionInit = lastUser ? Date.now() - lastUser.at : undefined;

    return (event: Event) => {
      const eventUserId = event.getUser().id;
      // a stored user that differs from the event's user belongs to someone else
      const isSameUser = Boolean(lastUser) && (!eventUserId || eventUserId === lastUser?.sub);
      event.addMetadata('AuthDiagnostics', {
        ...metadata,
        // time since Session initialized in this tab, or in any tab when this one has not
        msSinceLastSessionInit: isSameUser ? msSinceLastSessionInit : undefined,
      });
      if (lastUser && !eventUserId) {
        event.setUser(lastUser.sub);
      }
    };
  } catch {
    return () => {};
  }
}
